package com.ilussobsa.utils

import com.ilussobsa.Resources
import com.ilussobsa.Strings
import com.ilussobsa.views.DashboardScreen
import com.lightningkite.kiteui.Platform
import com.lightningkite.kiteui.ViewWrapper
import com.lightningkite.kiteui.current
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.pageNavigator
import com.lightningkite.kiteui.navigation.screenNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.reactive.PersistentProperty
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.l2.*
import com.lightningkite.reactive.context.*
import com.lightningkite.reactive.core.*
import com.lightningkite.reactive.extensions.*
import com.lightningkite.reactive.lensing.*
import com.lightningkite.readable.*
import kotlin.math.absoluteValue
import kotlin.time.Duration.Companion.seconds
import kotlinx.serialization.Serializable

val ferrariInspired = Theme(
    id = "ferrariDark",
    title = FontAndStyle(Resources.goldman),
    body = FontAndStyle(Resources.roboto),
    elevation = 0.dp,
    cornerRadii = CornerRadii.Constant(0.5.rem),
    gap = 0.75.rem,
    outline = Color.gray(0.8f),
    outlineWidth = 0.px,
    foreground = Color.gray(0.8f),
    background = RadialGradient(
        stops = listOf(
            GradientStop(0f, Color.gray(0.2f)),
            GradientStop(0.4f, Color.gray(0.1f)),
            GradientStop(1f, Color.gray(0.1f)),
        ),
    ),
    bar = { card() },
    dialog = { card() },
    card = {
        copy(background = this.background.closestColor().highlight(0.05f))
    },
    field = {
        copy(
            outlineWidth = 1.px,
            outline = foreground.closestColor().highlight(0.2f),
            cornerRadii = CornerRadii.ForceConstant(0.5.rem),
        )
    },
    nav = {
        copy(
            background = LinearGradient(
                stops = listOf(
                    GradientStop(0f, Color.gray(0.12f).withAlpha(0.5f)),
                    GradientStop(1f, Color.gray(0.1f).withAlpha(0.5f)),
                ),
                angle = Angle.halfTurn
            ),
        )
    }
)

val ferrariLight = Theme(
    id = "ferrariLight",
    title = FontAndStyle(Resources.goldman),
    body = FontAndStyle(Resources.roboto),
    elevation = 0.dp,
    cornerRadii = CornerRadii.Constant(0.5.rem),
    gap = 0.75.rem,
    outline = Color.gray(0.2f),
    outlineWidth = 0.px,
    foreground = Color.gray(0.2f),
    background = RadialGradient(
        stops = listOf(
            GradientStop(0f, Color.gray(1f)),
            GradientStop(0.4f, Color.gray(0.88f)),
            GradientStop(1f, Color.gray(0.88f)),
        ),
    ),
    field = {
        copy(
            outlineWidth = 1.px,
            cornerRadii = CornerRadii.ForceConstant(0.5.rem),
            outline = foreground.closestColor().highlight(0.2f)
        )
    },
    bar = { copy(background = this.background.closestColor().lighten(0.2f)) },
    dialog = { card() },
    card = {
        copy(background = this.background.closestColor().lighten(0.5f))
    },
    nav = {
        copy(
            background = LinearGradient(
                stops = listOf(
                    GradientStop(0f, Color.gray(0.90f).withAlpha(0.5f)),
                    GradientStop(1f, Color.gray(0.70f).withAlpha(0.5f)),
                ),
                angle = Angle.halfTurn
            ),
        )
    }
)

val offMarket = Theme(
    id = "offMarket",
    font = FontAndStyle(Resources.poppins),
    elevation = 0.dp,
    cornerRadii = CornerRadii.Constant(0.5.rem),
    gap = 0.75.rem,
    padding = Edges(0.75.rem),
    outline = Color.gray(0.75f),
    outlineWidth = 0.px,
    separatorOverride = Color.fromHex(0xd9d9d9),
    foreground = Color.black,
    background = Color.fromHex(0xf1f1f1),

    derivations = mapOf(
//        FieldSemantic to {
//            it.withBack(
////                revert = true,
//                outlineWidth = 1.dp,
//                cornerRadii = CornerRadii.Constant(0.5.rem),
//                outline = Color.black, //it.foreground.closestColor().highlight(0.2f)
//            )
//        },
        FieldSemantic to {
            it.copy(
                id = "fld",
                outlineWidth = 1.px,
                background = it.background.closestColor(),
                revert = true,
//                gap = it.gap / 2,
                cornerRadii = when (val base = it.cornerRadii) {
                    is CornerRadii.Constant -> CornerRadii.ForceConstant(base.value)
                    is CornerRadii.ForceConstant -> base
                    is CornerRadii.RatioOfSize -> base
                    is CornerRadii.RatioOfSpacing -> CornerRadii.ForceConstant(it.gap * base.value)
                    is CornerRadii.PerCorner -> base
                }
            ).withBack
        },
        OuterSemantic to {
            it.withoutBack(cascading = false, gap = 0.px, padding = Edges.ZERO)
        },
        MainContentSemantic to {
//            it.withoutBack(cascading = false, padding = Edges(1.5.rem), cornerRadii = CornerRadii.Constant(0.5.rem))
            // TODO: Workaround for KiteUI theme cascading bug
            it.withoutBack
        },
        SelectedSemantic to {
            it.withBack(
                background = Color.fromHex(0xf1f1f1),
                iconOverride = Color.fromHex(0xBB261A),
                font = it.font.copy(bold = true)
            )
        },
        UnselectedSemantic to { it.withBack },
        BarSemantic to {
            it.withBack(
                background = Color.white
            )
        },
        DialogSemantic to {
            it[CardSemantic]
        },
        CardSemantic to {
            it.withBack(
                background = Color.white,
                derivations = it.derivations + mapOf(
                    SelectedSemantic to {
                        it.withBack(background = Color.fromHex(0xf2f2f2))
                    },
                ),

                cornerRadii = CornerRadii.ForceConstant(0.5.rem)
            )
        },
        NavSemantic to {
            it.alter(
                background = Color.white,
                derivations = it.derivations + mapOf(
                    SelectedSemantic to {
                        it.withBack(
                            background = Color.fromHex(0xf2f2f2),
                            iconOverride = Color.fromHex(0xBB261A),
                            font = it.font.copy(bold = true),
                        )
                    }
                ),
            ).withBack(
                cascading = false,
                gap = 0.px,
                padding = Edges.ZERO,
            )
        },
        ImportantSemantic to {
            it.alter(cascading = false, gap = 0.5.rem, padding = Edges(0.75.rem))
                .withBack(
                    background = Color.fromHex(0xf1f1f1),
                    iconOverride = Color.fromHex(0x919191),
                    derivations = it.derivations + mapOf(
                        SelectedSemantic to {
                            it.withBack(
                                background = Color.fromHex(0x636363),
                                foreground = Color.white,
                                iconOverride = null,
                                font = it.font.copy(bold = true),
                            )
                        }
                    )
                )
        },
        ButtonSemantic to {
            it.withBack(cornerRadii = CornerRadii.ForceConstant(10.rem))
        },
        CriticalSemantic to {
            it.alter(cascading = false, gap = 0.px, padding = Edges.ZERO)
                .withBack(foreground = Color.fromHex(0x545454), derivations = it.derivations + mapOf(
                    ButtonSemantic to {
                        it.withBack(background = Color.white, foreground = Color.black, cornerRadii = CornerRadii.ForceConstant(10.rem), outline = Color.black, outlineWidth = 1.px, font = it.font.copy(bold = true), padding = Edges(1.rem, 0.rem, 1.rem, 0.rem))
                    },
                ))
        },
        DangerSemantic to {
            it.withoutBack(
                foreground = Color.fromHex(0xBB261A),
                font = it.font.copy(weight = 700),
                derivations = it.derivations + mapOf(
                    CardSemantic to {
                        it.withBack(background = Color.fromHex(0xBB261A), foreground = Color.white,  font = it.font.copy(bold = true), padding = Edges(0.5.rem))
                    },
                )
            )
        },
        HeaderSemantic to {
            it.alter(cascading = false, gap = 0.px, padding = Edges.ZERO).withoutBack(font = it.font.copy(bold = true))
        },
        WarningSemantic to {
            it.alter(cascading = false, gap = 0.px, padding = Edges.ZERO).withBack(foreground = Color.fromHex(0x919191), derivations = it.derivations + mapOf(
                ButtonSemantic to {
                    it.withBack(background = Color.black, foreground = Color.white, cornerRadii = CornerRadii.ForceConstant(10.rem), font = it.font.copy(bold = true), padding = Edges(1.rem, 0.25.rem, 1.rem, 0.25.rem))
                },
            ))
        },
        AffirmativeSemantic to {
            it.alter(cascading = false, gap = 0.px, padding = Edges.ZERO).withoutBack(foreground = Color.fromHex(0x1F5620), derivations = it.derivations + mapOf(
                ButtonSemantic to {
                    it.withBack(background = Color.fromHex(0x1F5620), foreground = Color.white, cornerRadii = CornerRadii.ForceConstant(0.75.rem), font = it.font.copy(bold = true), padding = Edges(1.rem, 0.rem, 1.rem, 0.rem))
                }
            ))
        },

//    important = { copy(background = Color.fromHex(0xf1f1f1)) },
//    critical = { copy(foreground = Color.fromHex(0x545454)) },
//    danger = {copy(foreground = Color.fromHex(0xBB261A), font = this.font.copy(weight = 700))}
    ),
//    field = {
//        copy(
//            outlineWidth = 1.px,
//            cornerRadii = CornerRadii.ForceConstant(0.5.rem),
//            outline = foreground.closestColor().highlight(0.2f)
//        )
//    },
//    selected = { copy(background = Color.fromHex(0xf1f1f1), iconOverride = Color.fromHex(0xBB261A), font = this.font.copy(bold = true) ) },
//    bar = { copy(background = Color.white) },
//    dialog = { card() },
//    card = {
//        copy(background = this.background.closestColor().lighten(0.75f))
//    },
//    nav = {
//        copy(
//            background = Color.white,
////            selected = Color.fromHex(0xf2f2f2)
//        )
//    },
)

//important critical warning
//red: #BB261A;danger
//green: #1F5620; affirmative
//grey light: #f1f1f1;
//grey mid-light: #D7D7D7;
//grey mid: #919191;  year mileage your proxy bid
//grey mid-dark: #545454;see more 7, bids, 93k, stating bid, 178k final bid setup payment


//Header 1 font-family: Stolzl;
//font-weight: 700;
//font-size: 32px;
//leading-trim: Cap height;
//line-height: 36px;
//letter-spacing: 0%;

//header 2 font-family: Poppins;
//font-weight: 700;
//font-size: 28px;
//line-height: 32px;
//letter-spacing: 0%;

//header 3: font-family: Poppins;
//font-weight: 700;
//font-size: 20px;
//line-height: 24px;
//letter-spacing: 0%;
//header 4: font-family: Poppins;
//font-weight: 700;
//font-size: 16px;
//line-height: 20px;
//letter-spacing: 0%;

//body: font-family: Poppins;
//font-weight: 400;
//font-size: 14px;
//line-height: 16px;
//letter-spacing: 0%;

//body bold: font-family: Poppins;
//font-weight: 700;
//font-size: 14px;
//line-height: 16px;
//letter-spacing: 0%;

//body small: font-family: Poppins;
//font-weight: 400;
//font-size: 11px;
//line-height: 13px;
//letter-spacing: 0%;

//body small bold: font-family: Poppins;
//font-weight: 700;
//font-size: 11px;
//line-height: 13px;
//letter-spacing: 0%;


//val accentHue = Angle(0.4)
val accentHue = Angle(0.56)
val flatDark = Theme.flat("flatDark", accentHue, baseBrightness = 0.1f, saturation = 0.7f).copy(
    title = FontAndStyle(Resources.goldman),
    body = FontAndStyle(Resources.roboto),
)
val flatDark2 = Theme.flat("flatDark2", accentHue, baseBrightness = 0.1f, saturation = 0.7f).copy(
    title = FontAndStyle(Resources.teko, additionalLetterSpacing = 0.3.rem),
    body = FontAndStyle(Resources.roboto),
)

val lightSaturation = 0.2f
val flatLight = Theme.flat("flatLight", accentHue, saturation = lightSaturation, baseBrightness = 0.9f).copy(
    title = FontAndStyle(Resources.goldman),
    body = FontAndStyle(Resources.roboto),
)

fun brandBasedExperimental(id: String, normalBack: Color = Color.gray(0.1f)): Theme {
    val startingBack = normalBack.highlight(0.05f)
//    val dominant = Color.fromHex(0x002b3f).toHSP()
    val dominant = Color.fromHex(0x000000).toHSP()
    val critical = Color.fromHex(0xCC0000).toHSP()
    val secondary = Color.fromHex(0xa2a2a2)
    val tertiary1 = Color.fromHex(0x5f5f5f).also {
        if (normalBack.perceivedBrightness < 0.5) it.toHSP().let { it.copy(brightness = 1f - it.brightness) }
            .toRGB() else it
    }
//    val tertiary2 = Color.fromHex(0x04A7CD).also {
//        if (normalBack.perceivedBrightness < 0.5) it.toHSP().let { it.copy(brightness = 1f - it.brightness) }
//            .toRGB() else it
//    }
    val tertiary2 = Color.fromHex(0xC7C7C7).also {
        if (normalBack.perceivedBrightness < 0.5) it.toHSP().let { it.copy(brightness = 1f - it.brightness) }
            .toRGB() else it
    }
    val tertiary3 = Color.fromHex(0xE3E3E3).also {
        if (normalBack.perceivedBrightness < 0.5) it.toHSP().let { it.copy(brightness = 1f - it.brightness) }
            .toRGB() else it
    }
//    val tertiary3 = Color.fromHex(0x97d0e2).also {
//        if (normalBack.perceivedBrightness < 0.5) it.toHSP().let { it.copy(brightness = 1f - it.brightness) }
//            .toRGB() else it
//    }
//    val tertiary4 = Color.fromHex(0x008EBC).also {
//        if (normalBack.perceivedBrightness < 0.5) it.toHSP().let { it.copy(brightness = 1f - it.brightness) }
//            .toRGB() else it
//    }
    val tertiary4 = Color.fromHex(0xBABABA).also {
        if (normalBack.perceivedBrightness < 0.5) it.toHSP().let { it.copy(brightness = 1f - it.brightness) }
            .toRGB() else it
    }
//    0x04A7CD
//    0x008EBC
    val assurance = if (normalBack.perceivedBrightness > 0.6f) Color.fromHex(0x04A7CD) else Color.fromHex(0x008EBC)
    val importanceRotation = listOf(tertiary3, normalBack, tertiary2, tertiary1, tertiary4).map { it.toHSP() }
    infix fun HSPColor.distanceTo(other: HSPColor): Float {
        return (this.hue - other.hue).absoluteValue.turns + (this.saturation - other.saturation).absoluteValue + (this.brightness - other.brightness).absoluteValue
    }

    fun Paint.outermostColor(): Color = when (this) {
        is Color -> this
        is FadingColor -> this.base
        is LinearGradient -> this.stops.first().color
        is RadialGradient -> this.stops.last().color
    }

    fun Paint.rotateImportance(): Paint {
        val hsp = outermostColor().toHSP()
        if (hsp.saturation > 0.5f) return hsp.toRGB().highlight(0.1f)
        return importanceRotation[
            importanceRotation.indices.minBy { importanceRotation[it] distanceTo hsp }.plus(1) % importanceRotation.size
        ].toRGB()
    }

    fun Paint.rotateBack(): Paint {
        val hsp = outermostColor().toHSP()
        if (hsp.saturation > 0.5f) return outermostColor().highlight(0.1f)
        return when {
            hsp.brightness < 0.99f -> normalBack
            else -> Color.gray(0.95f)
        }
    }

    fun Paint.deriveForeground(): Paint {
        val hsp = outermostColor().toHSP()
        return if (hsp.brightness > 0.6f) {
            if (dominant.brightness < 0.2f)
                dominant.toRGB()
            else
                Color.black
        } else {
            if (dominant.brightness > 0.8f)
                dominant.toRGB()
            else
                Color.white
        }
    }

    return Theme(
        id = id,
        title = FontAndStyle(Resources.stolzl),
        body = FontAndStyle(Resources.poppins),
        background = startingBack,
        elevation = 0.dp,
        transitionDuration = 0.25.seconds,
        cornerRadii = CornerRadii.Constant(0.5.rem),
        gap = 0.75.rem,
        outlineWidth = 0.px,
        foreground = startingBack.deriveForeground(),
        unselected = {
            val b = background.outermostColor().toHSP()
            if (b.saturation > 0.5 && (b.brightness - 0.5f).absoluteValue < 0.2f)
                copy(
                    id = "uns",
                    background = startingBack.highlight(0.1f),
                    foreground = startingBack.highlight(0.1f).deriveForeground(),
                )
            else null
        },
        selected = { important() },
        field = {
            copy(
                id = "fld",
                outline = background.outermostColor().highlight(0.3f),
                outlineWidth = 0.1.rem,
                cornerRadii = CornerRadii.ForceConstant(0.5.rem),
                background = background.outermostColor()
            )
        },
        focus = {
            copy(
                id = "fcs",
                outline = important().background,
                outlineWidth = 0.3.rem,
            )
        },
        bar = {
            val newBack = dominant.toRGB()
            copy(
                id = "bar",
                background = newBack,
                foreground = newBack.deriveForeground()
            )
        },
        important = {
            val newBack = background.rotateImportance()
            copy(
                id = "imp",
                background = newBack,
                foreground = newBack.deriveForeground()
            )
        },
        critical = {
            val newBack = critical.toRGB()
            copy(
                id = "crt",
                background = newBack,
                foreground = newBack.deriveForeground()
            )
        },
        card = {
            val newBack = background.rotateBack()
            copy(
                id = "crd",
                background = if (background !is Color) newBack.applyAlpha(0.87f) else newBack,
//                backdropFilters = if(background !is Color) listOf(BackdropFilter.Blur(1.rem)) else listOf(),
                foreground = newBack.deriveForeground()
            )
        },
        dialog = {
            copy(
                id = "dlg",
                background = background.outermostColor()
            )
        },
//        danger = {
//            copy(
//                background = Color.fromHex(0xFFB00020.toInt()),
//                outline = Color.fromHex(0xFFB00020.toInt()).highlight(0.1f),
//                foreground = Color.white
//            )
//        },
//        affirmative = {
//            copy(
//                background = Color.fromHex(0xFF20a020.toInt()),
//                outline = Color.fromHex(0xFF20a020.toInt()).highlight(0.1f),
//                foreground = Color.white
//            )
//        },
    )/*.let {
        it.customize(
            newId = it.id + "-start",
            background = startingBack.withStripe(),
            revert = true
        )
    }*/
}

@Serializable
enum class ThemePreference(val title: String, val icon: Icon, val theme: Theme) {
    OffMarket("Off Market", Icon.lightMode, offMarket),
    Brand("Brand Experimental", Icon.lightMode, brandBasedExperimental("brand", Color.white)),
    BrandDark("Brand Experimental Dark", Icon.darkMode, brandBasedExperimental("brand-dark", Color.gray(0.1f))),
    Dark(Strings.dark, Icon.darkMode, flatDark),
    Dark2(Strings.dark2, Icon.darkMode, flatDark2),
    Light(Strings.light, Icon.lightMode, flatLight),
    GrayscaleDark(Strings.grayscaleDark, Icon.darkMode, ferrariInspired),
    GrayscaleLight(Strings.grayscaleLight, Icon.lightMode, ferrariLight),
}

fun ViewWriter.raceStripe() = frame {
    themeChoice += ThemeDerivation {
        it.copy(
            "racestripe",
            background = LinearGradient(
                listOf(GradientStop(0f, Color.black), GradientStop(1f, Color.white))
            ),
        ).withBack
    }
    padding = 0.dp
    sizeConstraints(height = 10.dp, width = 100.dp) - atCenterEnd - rawImage(
        source = Resources.raceStripe,
        description = "",
        scaleType = ImageScaleType.Fit
    )
}

fun ViewWriter.appNavHamburger2(setup: AppNav.() -> Unit): ViewModifiable {
    val appNav = AppNav.ByProperty()
    val showMenu = Signal(false)
    return OuterSemantic.onNext - col {
        shownWhen { appNav.existsProperty() } - raceStripe()
        bar - row {
            showOnPrint = false
            setup(appNav)
            centered - toggleButton {
                checked bind showMenu
                icon(Icon.menu, "Open navigation menu")
            }
            if (Platform.current != Platform.Web) button {
                centered - icon(Icon.arrowBack, "Go Back")
                ::visible { pageNavigator.canGoBack() }
                onClick { pageNavigator.goBack() }
            }
            centered - sizeConstraints(height = 3.rem, width = 9.rem) - image {
                scaleType = ImageScaleType.Fit
                source = Resources.ilussoFullLogo(defaultAppTheme)
                description = Strings.appName
            }
            expanding - space()
            centered -navGroupActions(appNav.actionsProperty)
            ::shown { appNav.existsProperty() }
        }
        align(Align.Stretch, Align.Center) - shownWhen(false) { showMenu() && appNav.existsProperty() } - scrolling - navGroupColumn(appNav.navItemsProperty, { showMenu set false }) {
            gap = 0.px
        }
        expanding - OuterSemantic.onNext - frame {
            debugName = "menu and navigator container"
            navigatorView(pageNavigator)
        }
        shownWhen { appNav.existsProperty() } - footer()
    }
}

fun ViewWriter.appNavTopAndLeftWithLogo(setup: AppNav.() -> Unit): ViewModifiable {
    val appNav = AppNav.ByProperty()
    return OuterSemantic.onNext - col {
        shownWhen { appNav.existsProperty() } - raceStripe()
        bar - row {
            showOnPrint = false
            setup(appNav)
            if (Platform.current != Platform.Web) button {
                icon(Icon.arrowBack, "Go Back")
                ::visible { pageNavigator.canGoBack() }
                onClick { pageNavigator.goBack() }
            }

            centered - sizeConstraints(height = 3.rem, width = 9.rem) - image {
                scaleType = ImageScaleType.Fit
                source = Resources.ilussoFullLogo(defaultAppTheme)
                description = Strings.appName
            }
            expanding - space()
            navGroupActions(appNav.actionsProperty)

            ::shown { appNav.existsProperty() }
        }
        expanding - OuterSemantic.onNext - row {
            scrolls - navGroupColumn(appNav.navItemsProperty) {
                ::shown { appNav.navItemsProperty().size > 1 && appNav.existsProperty() }
                showOnPrint = false
            }
            expanding - navigatorView(pageNavigator)

        }
        shownWhen { appNav.existsProperty() } - footer()
    }
}

interface FullScreenAuction

fun ViewWriter.appNavAuctionJail(setup: AppNav.() -> Unit): ViewModifiable {
    val appNav = AppNav.ByProperty()
    return OuterSemantic.onNext - col {
        shownWhen { appNav.existsProperty() } - raceStripe()
        bar - row {
            showOnPrint = false
            setup(appNav)

            centered - sizeConstraints(height = 3.rem, width = 9.rem) - image {
                scaleType = ImageScaleType.Fit
                source = Resources.ilussoFullLogo(defaultAppTheme)
                description = Strings.appName
            }
            expanding - space()
            CriticalSemantic.plus(ButtonSemantic).onNext - button {
                centered - text(Strings.exit)
                onClick {
                    if (!pageNavigator.goBack()) {
                        pageNavigator.replace(DashboardScreen())
                    }
                }
            }
        }
        expanding - OuterSemantic.onNext - row {
            expanding - navigatorView(pageNavigator)
        }
        footer()
    }
}

@Serializable
enum class NavPreference(
    val title: String,
    val factory: ViewWriter.(setup: AppNav.() -> Unit) -> ViewModifiable,
    val worksForSmall: Boolean
) {
    Hamburger(Strings.collapsible, ViewWriter::appNavHamburger, true),
    TopAndLeft(Strings.alwaysOpen, ViewWriter::appNavTopAndLeftWithLogo, false),
    BottomTabs(Strings.bottomTabs, ViewWriter::appNavBottomTabs, true),
    Top(Strings.top, ViewWriter::appNavTop, false),
}

val appNavPreference = PersistentProperty<NavPreference?>("nav2", NavPreference.TopAndLeft)

val appThemePreference = Constant<ThemePreference?>(null)

val defaultAppTheme = ThemePreference.values().first().theme

val ViewWriter.tabTheme: ViewWrapper
    get() = card
val ViewWriter.navList: ViewWrapper
    get() = card

@ViewModifierDsl3
val ViewWriter.overimage: ViewWrapper
    get() = themeFromLast {
        it.copy(
            background = it.background.applyAlpha(0.75f),
        )
    }

data object BadgeSemantic : Semantic("bdg") {
    override fun default(theme: Theme): ThemeAndBack = theme[CardSemantic].theme.copy(
        id = key,
        foreground = theme.background,
        background = theme.foreground,
        padding = theme[CompactSemantic].theme.padding,
        gap = 6.dp,
        font = theme.font.copy(
            size = theme.font.size * 0.75,
            allCaps = true,
        ),
    ).withBack
}

data object IconToggleSemantic : Semantic("icntgl") {
    override fun default(theme: Theme): ThemeAndBack = theme.withoutBack(
        derivations = theme.derivations + mapOf(
            SelectedSemantic to {
                it.withoutBack(
                    iconOverride = null,
                    foreground = theme[DangerSemantic].theme.foreground,
                )
            }
        )
    )
}

@ViewModifierDsl3
val ViewWriter.iconButton: ViewWrapper
    get() = IconToggleSemantic.onNext

data object HighlightSemantic : Semantic("hlt") {
    // 172	53	38
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = key,
        foreground = Color(1f, 172 / 255f, 53 / 255f, 38 / 255f)
    ).withoutBack
}

data object LiveSemantic : Semantic("live") {
    // 46	85	39
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = HighlightSemantic.key,
        foreground = Color(1f, 46 / 255f, 85 / 255f, 39 / 255f),
        derivations = theme.derivations + mapOf(
            // Unsure why this is required
            ImportantSemantic to {
                ImportantSemantic.default(it)
            }
        )
    ).withoutBack
}

data object InactiveSemantic : Semantic("ina") {
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = HighlightSemantic.key,
        derivations = theme.derivations + mapOf(
            HighlightSemantic to {
                it.copy(
                    id = "hltina",
                    foreground = theme.foreground
                ).withoutBack
            },
            LiveSemantic to {
                it.copy(
                    id = "liveina",
                    foreground = theme.foreground
                ).withoutBack
            }
        )
    ).withoutBack
}

data object AuctionCTASemantic : Semantic("acta") {
    override fun default(theme: Theme): ThemeAndBack = theme[H1Semantic].theme.let { theme ->
        theme.copy(
            id = key,
            font = theme.font.copy(size = theme.font.size * 2, weight = 700)
        ).withoutBack
    }
}